import { useState, useEffect } from 'react';
import axios from '@/utils/api';
import { 
  Smile, TrendingUp, TrendingDown, Users, Trophy, 
  Bell, FileText, Sparkles, Moon, RefreshCw, BarChart3, Calendar
} from 'lucide-react';
import { MOOD_EMOJIS } from '@/utils/constants';

const API_BASE = '';

// Build a lookup map from the MOOD_EMOJIS constant for displaying actual app emojis
const getMoodDisplayData = (moodValue) => {
  const normalizedValue = moodValue?.toLowerCase()?.replace(/\s+/g, '_');
  const found = MOOD_EMOJIS.find(m => 
    m.value === normalizedValue || 
    m.value === moodValue ||
    m.label?.toLowerCase() === moodValue?.toLowerCase()
  );
  if (found) {
    return {
      emoji: found.emoji,
      label: found.label,
      isCustom: found.isCustom || false,
      imageSrc: found.imageSrc || null
    };
  }
  // Fallback for unknown moods
  return { 
    emoji: '😊', 
    label: moodValue?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown', 
    isCustom: false 
  };
};

// UnCloned mood icon component for analytics display
const UnClonedMoodIcon = ({ size = 24 }) => (
  <div 
    className="inline-block"
    style={{
      width: size,
      height: size,
      background: 'linear-gradient(90deg, #df1283 0%, #FF7901 25%, #D0FF0B 50%, #0FFFE2 75%, #df1283 100%)',
      backgroundSize: '200% 100%',
      animation: 'uncloned-gradient 3s ease infinite',
      WebkitMaskImage: 'url("/mood-uncloned.png")',
      WebkitMaskSize: 'contain',
      WebkitMaskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      maskImage: 'url("/mood-uncloned.png")',
      maskSize: 'contain',
      maskRepeat: 'no-repeat',
      maskPosition: 'center',
    }}
  />
);

// CSS for the animation (inject once)
const UnClonedStyles = () => (
  <style>{`
    @keyframes uncloned-gradient {
      0%, 100% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
    }
  `}</style>
);

const MoodAnalyticsDashboard = ({ isAdmin = false, theme = null }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('30d');

  // Default theme for admin
  const defaultTheme = {
    primary_color: '#df1283',
    secondary_color: '#FF7901',
    accent_color: '#0FFFE2',
    background_primary: '#2D063E',
    background_secondary: '#4A0A66',
    background_tertiary: '#1a0527',
    text_primary: '#FFFFFF',
    text_secondary: '#FDF2F8',
    border_color: '#6B0F8C',
    success_color: '#22C55E',
    warning_color: '#EAB308',
    error_color: '#EF4444'
  };

  const t = theme || defaultTheme;

  useEffect(() => {
    fetchMoodAnalytics();
  }, [isAdmin, dateRange]);

  const fetchMoodAnalytics = async () => {
    setLoading(true);
    try {
      const endpoint = isAdmin ? `/api/admin/mood-analytics?date_range=${dateRange}` : '/api/user/mood-trends';
      const res = await axios.get(`${API_BASE}${endpoint}`, { withCredentials: true });
      setData(res.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch mood analytics:', err);
      setError('Failed to load mood data');
    } finally {
      setLoading(false);
    }
  };

  const dateRangeOptions = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
    { value: '1y', label: '1 Year' },
    { value: 'all', label: 'All Time' }
  ];

  const getMoodColor = (mood) => {
    if (!mood) return `${t.text_secondary}40`;
    const colors = {
      1: t.error_color,
      2: t.warning_color,
      3: '#facc15',
      4: '#84cc16',
      5: t.success_color
    };
    return colors[Math.round(mood)] || t.accent_color;
  };

  const getMoodEmoji = (mood) => {
    if (!mood) return '😶';
    const emojis = { 1: '😢', 2: '😔', 3: '😐', 4: '😊', 5: '😄' };
    return emojis[Math.round(mood)] || '😐';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-8 h-8 animate-spin" style={{ color: t.text_secondary }} />
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className="rounded-xl p-8 text-center"
        style={{ background: t.background_secondary, border: `1px solid ${t.border_color}` }}
      >
        <Smile className="w-12 h-12 mx-auto mb-4" style={{ color: `${t.text_secondary}40` }} />
        <p style={{ color: t.text_secondary }}>{error}</p>
        <button 
          onClick={fetchMoodAnalytics}
          className="mt-4 px-4 py-2 rounded-lg text-sm"
          style={{ background: t.primary_color, color: 'white' }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data || (data.summary?.total_entries === 0)) {
    return (
      <div 
        className="rounded-xl p-8 text-center"
        style={{ background: t.background_secondary, border: `1px dashed ${t.border_color}` }}
      >
        <Smile className="w-16 h-16 mx-auto mb-4" style={{ color: `${t.text_secondary}30` }} />
        <h3 className="text-lg font-semibold mb-2" style={{ color: t.text_primary }}>
          No Mood Data Yet
        </h3>
        <p className="text-sm" style={{ color: t.text_secondary }}>
          {isAdmin 
            ? 'Users haven\'t tracked their mood yet. Mood data will appear here once users start logging their moods.'
            : 'Start tracking your mood in your journal entries to see your personal mood trends here!'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div 
            className="p-3 rounded-xl"
            style={{ background: `linear-gradient(135deg, ${t.primary_color}, ${t.secondary_color})` }}
          >
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold" style={{ color: t.text_primary }}>
              {isAdmin ? 'Site-Wide Mood Analytics' : 'Your Mood Trends'}
            </h2>
            <p className="text-sm" style={{ color: t.text_secondary }}>
              {isAdmin 
                ? `${data.summary?.users_tracking || 0} active users with entries • ${data.summary?.range_label || 'Last 30 Days'}`
                : `${data.summary?.days_tracked_this_week || 0} days tracked this week`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <div className="flex items-center gap-2 mr-2">
              <Calendar className="w-4 h-4" style={{ color: t.text_secondary }} />
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-3 py-1.5 rounded-lg text-sm border-none outline-none cursor-pointer"
                style={{ 
                  background: t.background_tertiary, 
                  color: t.text_primary,
                  border: `1px solid ${t.border_color}`
                }}
                data-testid="mood-date-range-select"
              >
                {dateRangeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}
          <button 
            onClick={fetchMoodAnalytics}
            className="p-2 rounded-lg hover:opacity-80"
            style={{ background: `${t.background_tertiary}80` }}
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" style={{ color: t.text_secondary }} />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Average Mood */}
        <div 
          className="p-5 rounded-xl"
          style={{ 
            background: `linear-gradient(135deg, ${t.background_secondary}, ${t.background_tertiary})`,
            border: `1px solid ${t.border_color}`
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm" style={{ color: t.text_secondary }}>
              {isAdmin ? 'Average Mood' : 'Your Average'}
            </span>
            <span className="text-2xl">{getMoodEmoji(data.summary?.average_mood)}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span 
              className="text-3xl font-bold"
              style={{ color: getMoodColor(data.summary?.average_mood) }}
            >
              {data.summary?.average_mood || '—'}
            </span>
            <span style={{ color: t.text_secondary }}>/5</span>
          </div>
        </div>

        {/* Total Entries */}
        <div 
          className="p-5 rounded-xl"
          style={{ 
            background: `linear-gradient(135deg, ${t.background_secondary}, ${t.background_tertiary})`,
            border: `1px solid ${t.border_color}`
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm" style={{ color: t.text_secondary }}>Mood Entries</span>
            <FileText className="w-5 h-5" style={{ color: t.accent_color }} />
          </div>
          <div className="text-3xl font-bold" style={{ color: t.text_primary }}>
            {data.summary?.total_entries || 0}
          </div>
        </div>

        {/* Tracking Users / Days */}
        <div 
          className="p-5 rounded-xl"
          style={{ 
            background: `linear-gradient(135deg, ${t.background_secondary}, ${t.background_tertiary})`,
            border: `1px solid ${t.border_color}`
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm" style={{ color: t.text_secondary }}>
              {isAdmin ? 'Users Tracking' : 'Days This Week'}
            </span>
            {isAdmin ? (
              <Users className="w-5 h-5" style={{ color: t.primary_color }} />
            ) : (
              <Trophy className="w-5 h-5" style={{ color: t.warning_color }} />
            )}
          </div>
          <div className="text-3xl font-bold" style={{ color: t.text_primary }}>
            {isAdmin ? data.summary?.users_tracking || 0 : `${data.summary?.days_tracked_this_week || 0}/7`}
          </div>
        </div>
      </div>

      {/* Weekly Trend Chart */}
      {data.weekly_trend && (
        <div 
          className="p-6 rounded-xl"
          style={{ background: t.background_secondary, border: `1px solid ${t.border_color}` }}
        >
          <h3 className="text-md font-semibold mb-4 flex items-center gap-2" style={{ color: t.text_primary }}>
            <TrendingUp className="w-4 h-4" style={{ color: t.accent_color }} />
            Weekly Mood Trend
          </h3>
          <div className="flex items-end gap-3 h-40">
            {data.weekly_trend.map((day, idx) => {
              const moodVal = isAdmin ? day.average : day.mood;
              const height = moodVal ? (moodVal / 5) * 100 : 0;
              const color = getMoodColor(moodVal);
              
              return (
                <div key={day.day || day.date || `trend-${idx}`} className="flex-1 flex flex-col items-center gap-2">
                  {moodVal && (
                    <span className="text-xs font-bold" style={{ color: t.text_primary }}>
                      {moodVal}
                    </span>
                  )}
                  <div 
                    className="w-full rounded-t-lg transition-all relative group"
                    style={{ 
                      height: `${Math.max(height, 8)}%`,
                      backgroundColor: color,
                      minHeight: moodVal ? '20px' : '8px',
                      opacity: moodVal ? 1 : 0.2
                    }}
                  >
                    {/* Tooltip */}
                    <div 
                      className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                      style={{ background: t.background_tertiary, color: t.text_primary }}
                    >
                      {moodVal ? `${getMoodEmoji(moodVal)} ${moodVal}/5` : 'No data'}
                      {isAdmin && day.count > 0 && ` (${day.count} entries)`}
                    </div>
                  </div>
                  <span className="text-xs" style={{ color: `${t.text_secondary}80` }}>{day.day}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AI Insights */}
      {data.insights && data.insights.length > 0 && (
        <div 
          className="p-6 rounded-xl"
          style={{ 
            background: `linear-gradient(135deg, ${t.background_secondary}, ${t.primary_color}10)`,
            border: `1px solid ${t.primary_color}40`
          }}
        >
          <h3 className="text-md font-semibold mb-4 flex items-center gap-2" style={{ color: t.text_primary }}>
            <Sparkles className="w-4 h-4" style={{ color: t.primary_color }} />
            {isAdmin ? 'AI Insights' : 'Personal Insights'}
            <span 
              className="text-xs px-2 py-0.5 rounded-full"
              style={{ 
                background: `linear-gradient(135deg, ${t.primary_color}, ${t.secondary_color})`,
                color: 'white'
              }}
            >
              Coach
            </span>
          </h3>
          <div className="space-y-3">
            {data.insights.map((insight, idx) => {
              const iconColors = {
                positive: t.success_color,
                warning: t.warning_color,
                attention: t.error_color,
                info: t.accent_color
              };
              const bgColors = {
                positive: `${t.success_color}15`,
                warning: `${t.warning_color}15`,
                attention: `${t.error_color}15`,
                info: `${t.accent_color}15`
              };
              const iconColor = iconColors[insight.type] || t.accent_color;
              const bgColor = bgColors[insight.type] || `${t.accent_color}15`;
              
              const IconComponent = {
                trophy: Trophy,
                alert: Bell,
                trending_up: TrendingUp,
                trending_down: TrendingDown,
                users: Users,
                clipboard: FileText,
                sparkles: Sparkles,
                heart: Smile,
                check: Trophy
              }[insight.icon] || Sparkles;
              
              return (
                <div 
                  key={insight.title || `insight-${idx}`}
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: bgColor }}
                >
                  <div className="flex items-start gap-3">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${iconColor}30` }}
                    >
                      <IconComponent className="w-4 h-4" style={{ color: iconColor }} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium mb-1" style={{ color: t.text_primary }}>
                        {insight.title}
                      </h4>
                      <p className="text-sm" style={{ color: `${t.text_secondary}CC` }}>
                        {insight.message}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Individual Users (Admin Only) */}
      {isAdmin && data.individual_users && data.individual_users.length > 0 && (
        <div 
          className="p-6 rounded-xl"
          style={{ background: t.background_secondary, border: `1px solid ${t.border_color}` }}
        >
          <h3 className="text-md font-semibold mb-4 flex items-center gap-2" style={{ color: t.text_primary }}>
            <Users className="w-4 h-4" style={{ color: t.primary_color }} />
            Top Users by Mood Tracking
          </h3>
          <div className="space-y-3">
            {data.individual_users.map((user, idx) => (
              <div 
                key={user.user_id || user.email || `user-${idx}`}
                className="flex items-center justify-between p-3 rounded-lg"
                style={{ backgroundColor: `${t.background_tertiary}50` }}
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                    style={{ 
                      background: `linear-gradient(135deg, ${t.primary_color}, ${t.secondary_color})`,
                      color: 'white'
                    }}
                  >
                    {user.name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <p className="font-medium" style={{ color: t.text_primary }}>{user.name}</p>
                    <p className="text-xs" style={{ color: `${t.text_secondary}80` }}>
                      {user.entry_count} mood entries
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xl">{getMoodEmoji(user.average_mood)}</span>
                  <span 
                    className="text-lg font-bold"
                    style={{ color: getMoodColor(user.average_mood) }}
                  >
                    {user.average_mood}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Entry Engagement - Users with entries vs without */}
      {data.engagement && (
        <div 
          className="p-6 rounded-xl"
          style={{ background: t.background_secondary, border: `1px solid ${t.border_color}` }}
        >
          <h3 className="text-md font-semibold mb-4 flex items-center gap-2" style={{ color: t.text_primary }}>
            <Users className="w-5 h-5" style={{ color: t.primary_color }} />
            Entry Engagement
          </h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-4 rounded-lg" style={{ background: t.background_tertiary }}>
              <p className="text-2xl font-bold" style={{ color: t.text_primary }}>{data.engagement.total_users}</p>
              <p className="text-xs" style={{ color: `${t.text_secondary}80` }}>Total Users</p>
            </div>
            <div className="text-center p-4 rounded-lg" style={{ background: t.background_tertiary }}>
              <p className="text-2xl font-bold" style={{ color: t.success_color }}>{data.engagement.users_with_entries}</p>
              <p className="text-xs" style={{ color: `${t.text_secondary}80` }}>Have Created Entries</p>
            </div>
            <div className="text-center p-4 rounded-lg" style={{ background: t.background_tertiary }}>
              <p className="text-2xl font-bold" style={{ color: t.warning_color }}>{data.engagement.users_without_entries}</p>
              <p className="text-xs" style={{ color: `${t.text_secondary}80` }}>Never Created Entry</p>
            </div>
          </div>
          
          {/* Progress bar showing activation rate */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs" style={{ color: `${t.text_secondary}80` }}>
              <span>Activation Rate</span>
              <span style={{ color: t.success_color }}>{data.engagement.activation_rate}%</span>
            </div>
            <div className="w-full h-3 rounded-full overflow-hidden" style={{ background: t.background_tertiary }}>
              <div 
                className="h-full rounded-full transition-all"
                style={{ 
                  width: `${data.engagement.activation_rate}%`,
                  background: `linear-gradient(90deg, ${t.primary_color}, ${t.success_color})`
                }}
              />
            </div>
            <p className="text-xs mt-2" style={{ color: `${t.text_secondary}60` }}>
              {data.engagement.users_without_entries} users have signed up but never created a journal entry. Consider sending them a nudge!
            </p>
          </div>
        </div>
      )}

      {/* Mood Distribution - All Individual Moods */}
      {data.label_distribution && Object.keys(data.label_distribution).length > 0 && (
        <div 
          className="p-6 rounded-xl"
          style={{ background: t.background_secondary, border: `1px solid ${t.border_color}` }}
        >
          <UnClonedStyles />
          <h3 className="text-md font-semibold mb-2" style={{ color: t.text_primary }}>
            Mood Usage
          </h3>
          <p className="text-xs mb-4" style={{ color: `${t.text_secondary}60` }}>
            How often each mood is selected by active users
          </p>
          
          {/* Grid of all moods with counts */}
          <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7 gap-3">
            {Object.entries(data.label_distribution)
              .sort((a, b) => b[1] - a[1])
              .map(([mood, count]) => {
                const total = Object.values(data.label_distribution).reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                const moodData = getMoodDisplayData(mood);
                
                return (
                  <div 
                    key={mood}
                    className="flex flex-col items-center p-3 rounded-lg transition-all hover:scale-105"
                    style={{ 
                      background: t.background_tertiary,
                      border: `1px solid ${count > 0 ? t.primary_color + '40' : t.border_color + '30'}`
                    }}
                  >
                    {/* Use actual mood icon from the app */}
                    {moodData.isCustom ? (
                      <UnClonedMoodIcon size={32} />
                    ) : (
                      <span className="text-2xl mb-1">{moodData.emoji}</span>
                    )}
                    <span className="text-xs font-medium capitalize" style={{ color: t.text_primary }}>
                      {moodData.label}
                    </span>
                    <span className="text-lg font-bold mt-1" style={{ color: count > 0 ? t.accent_color : `${t.text_secondary}40` }}>
                      {count}
                    </span>
                    <span className="text-xs" style={{ color: `${t.text_secondary}60` }}>
                      {percentage}%
                    </span>
                  </div>
                );
              })}
          </div>
          
          {/* Summary stats */}
          <div className="mt-4 pt-4 flex justify-between text-xs" style={{ borderTop: `1px solid ${t.border_color}40`, color: `${t.text_secondary}60` }}>
            <span>Total mood selections: <strong style={{ color: t.text_primary }}>{Object.values(data.label_distribution).reduce((a, b) => a + b, 0)}</strong></span>
            <span>Unique moods used: <strong style={{ color: t.text_primary }}>{Object.keys(data.label_distribution).filter(k => data.label_distribution[k] > 0).length}</strong></span>
          </div>
        </div>
      )}

      {/* Moods Never Used */}
      {data.unused_moods && data.unused_moods.length > 0 && (
        <div 
          className="p-6 rounded-xl"
          style={{ background: t.background_secondary, border: `1px solid ${t.warning_color}40` }}
        >
          <h3 className="text-md font-semibold mb-2 flex items-center gap-2" style={{ color: t.warning_color }}>
            <Bell className="w-4 h-4" />
            Moods Never Used
          </h3>
          <p className="text-xs mb-4" style={{ color: `${t.text_secondary}60` }}>
            Consider removing these or promoting them to users
          </p>
          <div className="flex flex-wrap gap-2">
            {data.unused_moods.map(mood => {
              const moodData = getMoodDisplayData(mood);
              return (
                <div 
                  key={mood}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg"
                  style={{ background: t.background_tertiary }}
                >
                  {moodData.isCustom ? (
                    <UnClonedMoodIcon size={20} />
                  ) : (
                    <span className="text-lg">{moodData.emoji}</span>
                  )}
                  <span className="text-xs capitalize" style={{ color: `${t.text_secondary}80` }}>
                    {moodData.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MoodAnalyticsDashboard;
